import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { apiGet } from "@/lib/apiClient";
import { formatCrmSourceLabel, formatCrmStatusLabel } from "@/lib/crmDisplay";
import { Building2, User } from "lucide-react";
import { CrmAccountEditDialog, type AccountEditSnapshot } from "@/components/crm/CrmAccountEditDialog";
import { CrmActivityPanel, type ActivityTouchpoint } from "@/components/crm/CrmActivityPanel";
import { ConnectedRecordsPanel } from "@/components/crm/ConnectedRecordsPanel";
import { CrmRecordDetailLayout } from "@/components/crm/CrmRecordDetailLayout";
import { resolveCrmRecordTab } from "@/components/crm/CrmRecordTabs";
import { RecordTimeline, type RecordTimelineEvent } from "@/components/crm/RecordTimeline";
import { crmContactDisplayName } from "@/lib/crmContactParty";

type Account = AccountEditSnapshot & {
  id: string;
  accountKind?: string | null;
  financeCustomer?: boolean;
};

type ContactRow = {
  id: string;
  fullName?: string | null;
  email?: string | null;
  title?: string | null;
  firstName?: string | null;
  lastName?: string | null;
};
type DealRow = { id: string; name?: string | null; stage?: string | null; amount?: number | null };
type LeadRow = { id: string; leadName?: string; stage?: string; status?: string; accountId?: string | null };

export function CrmAccountDetailPage() {
  const { accountId: idParam } = useParams<{ accountId: string }>();
  const accountId = idParam ? decodeURIComponent(idParam) : "";
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = resolveCrmRecordTab(searchParams.get("tab"));
  const editOpen = searchParams.get("edit") === "1" || searchParams.get("edit") === "true";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [account, setAccount] = useState<Account | null>(null);
  const [contacts, setContacts] = useState<ContactRow[]>([]);
  const [deals, setDeals] = useState<DealRow[]>([]);
  const [leads, setLeads] = useState<LeadRow[]>([]);
  const [touchpoints, setTouchpoints] = useState<ActivityTouchpoint[]>([]);
  const [timeline, setTimeline] = useState<RecordTimelineEvent[]>([]);

  const tabSearch = useMemo(() => {
    const p = new URLSearchParams(searchParams);
    p.delete("tab");
    p.delete("edit");
    return p;
  }, [searchParams]);

  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const [accRes, contactsRes, dealsRes, leadsRes, tpRes] = await Promise.all([
        apiGet<Account>(`/api/crm/accounts/${encodeURIComponent(accountId)}`),
        apiGet<{ contacts?: ContactRow[] }>(`/api/crm/contacts?accountId=${encodeURIComponent(accountId)}`),
        apiGet<{ deals?: DealRow[] }>(`/api/crm/deals?accountId=${encodeURIComponent(accountId)}`),
        apiGet<{ leads?: LeadRow[] }>("/api/crm/leads?limit=200"),
        apiGet<{ touchpoints?: ActivityTouchpoint[] }>(
          `/api/crm/touchpoints?accountId=${encodeURIComponent(accountId)}&limit=100`,
        ),
      ]);
      if (cancelled) return;
      if (!accRes.ok || !accRes.data) {
        setError(accRes.error || "Account not found.");
        setAccount(null);
      } else {
        setAccount(accRes.data);
        setError(null);
      }
      setContacts(contactsRes.ok ? (contactsRes.data?.contacts ?? []) : []);
      setDeals(dealsRes.ok ? (dealsRes.data?.deals ?? []) : []);
      const allLeads = leadsRes.ok ? (leadsRes.data?.leads ?? []) : [];
      setLeads(allLeads.filter((l) => l.accountId === accountId));
      setTouchpoints(tpRes.ok ? (tpRes.data?.touchpoints ?? []) : []);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  useEffect(() => {
    if (!accountId || activeTab !== "history") {
      setTimeline([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      const res = await apiGet<{ events?: RecordTimelineEvent[] }>(
        `/api/audit/app/crm/module/accounts/record/${encodeURIComponent(accountId)}?limit=120`,
      );
      if (cancelled) return;
      setTimeline(res.ok ? (res.data?.events ?? []) : []);
    })();
    return () => {
      cancelled = true;
    };
  }, [accountId, activeTab]);

  const setEditOpen = (open: boolean) => {
    const next = new URLSearchParams(searchParams);
    if (open) next.set("edit", "1");
    else next.delete("edit");
    setSearchParams(next);
  };

  if (loading) return <p className="text-sm text-slate-500">Loading account…</p>;
  if (error && !account) {
    return (
      <div className="space-y-3">
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">{error}</div>
        <Link to="/crm/accounts" className="text-sm text-indigo-600 hover:underline">
          Back to accounts
        </Link>
      </div>
    );
  }
  if (!account) return null;

  const basePath = `/crm/accounts/${encodeURIComponent(account.id)}`;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-gray-500">Account detail</p>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            {account.accountKind === "individual" ? (
              <User className="size-8 text-indigo-600" aria-hidden />
            ) : (
              <Building2 className="size-8 text-indigo-600" aria-hidden />
            )}
            {account.name}
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Status {formatCrmStatusLabel(account.status)} · Stage {formatCrmStatusLabel(account.stage)} · Source{" "}
            {formatCrmSourceLabel(account.source)}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setEditOpen(true)}
            className="rounded-md border border-indigo-300 bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-800 hover:bg-indigo-100 dark:border-indigo-400/50 dark:bg-indigo-900/30 dark:text-indigo-100"
          >
            Edit account
          </button>
          <Link
            to={`/crm/deals/new?accountId=${encodeURIComponent(account.id)}`}
            className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500"
          >
            New deal
          </Link>
          <Link
            to={`/crm/contacts/new?accountId=${encodeURIComponent(account.id)}`}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold hover:bg-gray-50 dark:border-white/20"
          >
            New contact
          </Link>
          <Link to="/crm/accounts" className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium hover:bg-gray-50 dark:border-white/20">
            Back
          </Link>
        </div>
      </div>

      <CrmAccountEditDialog
        accountId={account.id}
        initial={account}
        open={editOpen}
        onOpenChange={setEditOpen}
        onSaved={(updated) => setAccount((prev) => (prev ? { ...prev, ...updated } : prev))}
      />

      <CrmRecordDetailLayout
        basePath={basePath}
        activeTab={activeTab}
        searchParams={tabSearch}
        connectedRecords={
          <ConnectedRecordsPanel
            compact
            selectedRecordId={account.id}
            accounts={[
              {
                id: account.id,
                label: account.name,
                href: basePath,
                meta: account.domain || account.email || account.id,
                icon: account.accountKind === "individual" ? "user" : "building",
              },
            ]}
            contacts={contacts.map((c) => ({
              id: c.id,
              label: crmContactDisplayName(c),
              href: `/crm/contacts/${encodeURIComponent(c.id)}?accountId=${encodeURIComponent(account.id)}`,
              meta: c.title || c.email || "—",
            }))}
            deals={deals.map((d) => ({
              id: d.id,
              label: d.name || d.id,
              href: `/crm/deals/${encodeURIComponent(d.id)}?accountId=${encodeURIComponent(account.id)}`,
              meta: formatCrmStatusLabel(d.stage),
            }))}
            leads={leads.map((l) => ({
              id: l.id,
              label: l.leadName || l.id,
              href: `/crm/leads/${encodeURIComponent(l.id)}`,
              meta: `${formatCrmStatusLabel(l.stage)} · ${formatCrmStatusLabel(l.status)}`,
            }))}
            createContactHref={`/crm/contacts/new?accountId=${encodeURIComponent(account.id)}`}
            createDealHref={`/crm/deals/new?accountId=${encodeURIComponent(account.id)}`}
            hideEmptyLeads
          />
        }
      >
        {activeTab === "overview" ? (
          <div className="space-y-4">
            <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-white/5">
              <h2 className="text-lg font-semibold">Account overview</h2>
              <dl className="mt-3 grid gap-3 text-sm md:grid-cols-2">
                <div>
                  <dt className="text-gray-500">Domain</dt>
                  <dd className="font-medium">{account.domain || "—"}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Website</dt>
                  <dd className="font-medium">{account.website || "—"}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Email</dt>
                  <dd className="font-medium">{account.email || "—"}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Phone</dt>
                  <dd className="font-medium">{account.companyPhone || "—"}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Location</dt>
                  <dd className="font-medium">
                    {[account.city, account.state].filter(Boolean).join(", ") || "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Owner</dt>
                  <dd className="font-medium">{account.ownerId || "Unassigned"}</dd>
                </div>
              </dl>
            </section>

            <CrmActivityPanel
              accountId={account.id}
              initialTouchpoints={touchpoints}
              contacts={contacts.map((c) => ({
                id: c.id,
                label: crmContactDisplayName(c),
              }))}
              title="Account activity"
            />

            <div className="grid gap-4 lg:grid-cols-2">
              <section className="rounded-lg border border-gray-200 dark:border-white/10">
                <h2 className="border-b px-4 py-2 text-sm font-semibold dark:border-white/10">Contacts</h2>
                <ul className="divide-y dark:divide-white/5">
                  {contacts.length === 0 ? (
                    <li className="px-4 py-4 text-sm text-slate-500">No contacts.</li>
                  ) : (
                    contacts.map((c) => (
                      <li key={c.id} className="px-4 py-3">
                        <Link
                          to={`/crm/contacts/${encodeURIComponent(c.id)}?accountId=${encodeURIComponent(account.id)}`}
                          className="font-medium text-indigo-600 hover:underline dark:text-indigo-300"
                        >
                          {crmContactDisplayName(c)}
                        </Link>
                        <p className="text-xs text-slate-500">{c.title || "—"}</p>
                      </li>
                    ))
                  )}
                </ul>
              </section>
              <section className="rounded-lg border border-gray-200 dark:border-white/10">
                <h2 className="border-b px-4 py-2 text-sm font-semibold dark:border-white/10">Deals</h2>
                <ul className="divide-y dark:divide-white/5">
                  {deals.length === 0 ? (
                    <li className="px-4 py-4 text-sm text-slate-500">No deals.</li>
                  ) : (
                    deals.map((d) => (
                      <li key={d.id} className="px-4 py-3">
                        <Link
                          to={`/crm/deals/${encodeURIComponent(d.id)}?accountId=${encodeURIComponent(account.id)}`}
                          className="font-medium text-indigo-600 hover:underline dark:text-indigo-300"
                        >
                          {d.name || d.id}
                        </Link>
                        <p className="text-xs text-slate-500">
                          {formatCrmStatusLabel(d.stage)} · {d.amount != null ? d.amount.toLocaleString() : "—"}
                        </p>
                      </li>
                    ))
                  )}
                </ul>
              </section>
            </div>
          </div>
        ) : (
          <section className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-lg font-semibold">History</h2>
              <Link to={`/crm/audit/accounts/${encodeURIComponent(account.id)}`} className="text-sm text-indigo-600 hover:underline">
                Open full audit page
              </Link>
            </div>
            <RecordTimeline events={timeline} emptyMessage="No record history for this account yet." />
          </section>
        )}
      </CrmRecordDetailLayout>
    </div>
  );
}
